import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { setToken, getToken, clearToken } from '@/lib/auth';

import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (token) {
      axios.get(`${API}/auth/me`, { withCredentials: true })
        .then(() => navigate('/dashboard', { replace: true }))
        .catch((err) => {
          // Only clear token on definitive 401, not network errors
          if (err.response?.status === 401) {
            clearToken();
          }
          setCheckingAuth(false);
        });
    } else {
      setCheckingAuth(false);
    }
  }, [navigate]);

  if (checkingAuth) {
    return (
      <div className="min-h-screen hero-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (response.data.session_token) {
        setToken(response.data.session_token);
      }
      toast.success('Logged in successfully!');
      navigate('/dashboard', { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4">
      <div className="noise-texture absolute inset-0 pointer-events-none"></div>
      <div className="relative z-10 bg-white rounded-md p-8 w-full max-w-md card-shadow">
        <div className="text-center mb-6">
          <div className="text-6xl mb-2">☘️</div>
          <h1 className="font-bebas text-4xl text-emerald-900 tracking-wider">Welcome Back</h1>
          <p className="font-manrope text-gray-600">Sign in to your Shamrock Pool account</p>
        </div>

        <button
          data-testid="google-login-button"
          onClick={handleGoogleLogin}
          className="w-full btn-secondary py-3 rounded-md mb-6 flex items-center justify-center space-x-2"
        >
          <span>Continue with Google</span>
        </button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500 font-manrope">Or continue with</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="font-manrope">Email</Label>
            <Input
              id="email"
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="password" className="font-manrope">Password</Label>
            <Input
              id="password"
              data-testid="password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1"
            />
            <div className="text-right mt-1">
              <Link to="/forgot-password" className="text-sm font-manrope text-amber-600 hover:text-amber-700">
                Forgot password?
              </Link>
            </div>
          </div>
          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full btn-primary py-3 rounded-md"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <p className="text-center mt-6 font-manrope text-gray-600">
          Don't have an account?{' '}
          <Link to="/register" data-testid="register-link" className="text-emerald-900 font-bold hover:text-amber-600">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}